import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { TopBar } from "../components/TopBar";
import { TabBar } from "../components/TabBar";
import { api } from "../api/client";
import type { ProgramDetail, WorkoutTemplate } from "../api/types";

export const ProgramEditor = () => {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const [program, setProgram] = useState<ProgramDetail | null>(null);
  const [templates, setTemplates] = useState<WorkoutTemplate[]>([]);
  const [picker, setPicker] = useState(false);
  const [pickerQ, setPickerQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api<ProgramDetail>(`/programs/${id}`).then(setProgram).catch(() => {});
    api<WorkoutTemplate[]>("/templates").then(setTemplates).catch(() => {});
  }, [id]);

  const tplById = useMemo(() => new Map(templates.map((t) => [t.id, t])), [templates]);

  if (!program) return <div className="page center">Loading…</div>;

  const update = (patch: Partial<ProgramDetail>) => setProgram({ ...program, ...patch });

  const addPlan = (templateId: string) => {
    update({ plans: [...program.plans, templateId] });
    setPicker(false);
  };

  const removePlanAt = (i: number) =>
    update({ plans: program.plans.filter((_, idx) => idx !== i) });

  const movePlan = (i: number, dir: -1 | 1) => {
    const next = [...program.plans];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    update({ plans: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api(`/programs/${id}`, {
        method: "PUT",
        body: {
          name: program.name,
          description: program.description,
          plans: program.plans,
        },
      });
      nav(-1);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="page">
        <TopBar
          title="Edit program"
          back
          right={
            <button className="btn small" disabled={saving} onClick={() => void save()}>
              {saving ? "Saving…" : "Save"}
            </button>
          }
        />

        <label className="field">
          Name
          <input value={program.name} onChange={(e) => update({ name: e.target.value })} />
        </label>
        <label className="field" style={{ marginTop: 12 }}>
          Description
          <textarea
            rows={2}
            value={program.description ?? ""}
            onChange={(e) => update({ description: e.target.value })}
          />
        </label>

        <h2 className="h2">Plans in order</h2>
        <div className="muted" style={{ marginBottom: 8 }}>
          The next plan to start is highlighted. After you finish a workout from this program, the next plan
          advances automatically (and cycles back to the top at the end).
        </div>

        <div className="col">
          {program.plans.length === 0 && (
            <div className="muted">No plans yet. Add a plan to start sequencing your program.</div>
          )}
          {program.plans.map((tplId, i) => {
            const t = tplById.get(tplId);
            const isNext = program.next?.index === i;
            return (
              <div
                className="card"
                key={`${tplId}-${i}`}
                style={isNext ? { borderColor: "var(--orange)", borderWidth: 2 } : undefined}
              >
                <div className="row">
                  <div className="thumb">{(t?.name ?? "?").charAt(0).toUpperCase()}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>{t?.name ?? `Deleted plan (${tplId})`}</div>
                    <div className="muted">
                      Position {i + 1}
                      {isNext && (
                        <>
                          {" · "}
                          <span className="chip solid">Next up</span>
                        </>
                      )}
                    </div>
                  </div>
                  <button className="btn small secondary" onClick={() => movePlan(i, -1)}>↑</button>
                  <button className="btn small secondary" onClick={() => movePlan(i, 1)}>↓</button>
                  <button className="btn small secondary" onClick={() => removePlanAt(i)}>✕</button>
                </div>
              </div>
            );
          })}
        </div>

        <button className="btn" style={{ marginTop: 12 }} onClick={() => setPicker(true)}>
          + Add plan
        </button>
      </div>

      {picker && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 30,
            display: "flex",
            alignItems: "flex-end",
          }}
          onClick={() => setPicker(false)}
        >
          <div
            style={{
              background: "white",
              width: "100%",
              maxHeight: "80vh",
              overflowY: "auto",
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              padding: 16,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="h2" style={{ marginTop: 0 }}>Add plan</h2>
            <input
              autoFocus
              placeholder="Search plans…"
              value={pickerQ}
              onChange={(e) => setPickerQ(e.target.value)}
            />
            <div style={{ marginTop: 12 }}>
              {templates.length === 0 && (
                <div className="muted">You haven't created any plans yet.</div>
              )}
              {templates
                .filter(
                  (t) => !pickerQ || t.name.toLowerCase().includes(pickerQ.toLowerCase()),
                )
                .map((t) => (
                  <button key={t.id} className="list-card" onClick={() => addPlan(t.id)}>
                    <div className="thumb">{t.name.charAt(0).toUpperCase()}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700 }}>{t.name}</div>
                      <div className="muted">{t.exercises.length} exercises</div>
                    </div>
                    <span className="chip">Add</span>
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}
      <TabBar />
    </>
  );
};
